import * as React from 'react';
import { useMemo, useContext, useEffect } from 'react';
import { FormControl, Select, MenuItem } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { getComponentElementKeyValue } from '../utils/key-value-getter';
import { BaseFormStateContext } from '../BaseForm';
import { ComponentInputElement } from '../../types/common/item-definition-types';
import { ItemValueKey } from '../../config/item-value-key';
import DateUtils from '../../utils/date-utils';

interface P {
  componentElement: ComponentInputElement;
}

const GAS_START_TIME_EVE_VALUE = '3'; // ガス開栓時間選択肢の「ＥＶＥ（１７-１９時）」
const GAS_START_TIME_DEFAULT_VALUE = '0'; // ガス開栓時間選択肢の初期値「ＡＭ」

const useStyles = makeStyles(
  createStyles({
    selectPlaceholder: {
      color: '#BDBDBD'
    }
  })
);

const SelectItemElement: React.FC<P> = ({ componentElement }) => {
  const styles = useStyles({});
  const { inputValues, updateInputValues, applicationParams } = useContext(BaseFormStateContext);

  const valueKey = useMemo(() => componentElement.valueKey, []);

  // ガス開栓日が日曜・祝日なら、EVE(value='3')を無効化するためのフラグ
  const isGasEveDisabled = useMemo(() => {
    if (valueKey !== ItemValueKey.GasStartTimeSelection) {
      return false;
    }
    const gasStartDate = inputValues[ItemValueKey.GasStartDate] as string | undefined;
    if (!gasStartDate) {
      return false;
    }
    return DateUtils.isSundayOrHoliday(gasStartDate);
  }, [valueKey, inputValues[ItemValueKey.GasStartDate]]);

  // 日祝のガス開栓時間選択では EVE(value='3') を選択肢から除外する
  const selectionItems = useMemo(() => {
    if (!componentElement.selectionItems) {
      return componentElement.selectionItems;
    }
    if (valueKey === ItemValueKey.GasStartTimeSelection && isGasEveDisabled) {
      return componentElement.selectionItems.filter(item => item.value !== GAS_START_TIME_EVE_VALUE);
    }
    return componentElement.selectionItems;
  }, [componentElement.selectionItems, valueKey, isGasEveDisabled]);

  const inputValue = useMemo(
    () =>
      getComponentElementKeyValue({
        inputValues,
        applicationParams,
        inputValueType: componentElement.valueType,
        valueKey: valueKey,
        initialValue: componentElement.initialValue,
        converter: componentElement.converter
      }),
    [
      inputValues[valueKey],
      applicationParams[valueKey],
      componentElement.valueType,
      valueKey,
      componentElement.initialValue
    ]
  );

  // 初期値の補完
  useEffect(() => {
    if (inputValues[valueKey] || !componentElement.initialValue) {
      return;
    }
    updateInputValues(valueKey, componentElement.initialValue);
  }, []);

  // 日祝でEVEが選択不可になった場合、既に選択済みのEVE(value='3')を初期値へ戻す
  useEffect(() => {
    if (valueKey !== ItemValueKey.GasStartTimeSelection) {
      return;
    }
    if (!isGasEveDisabled) {
      return;
    }
    if (inputValues[valueKey] === GAS_START_TIME_EVE_VALUE) {
      updateInputValues(valueKey, componentElement.initialValue || GAS_START_TIME_DEFAULT_VALUE);
    }
  }, [valueKey, isGasEveDisabled, inputValues[valueKey], componentElement.initialValue]);

  return (
    <FormControl margin="normal" variant="outlined" style={{ ...componentElement.styleOption, width: '100%' }}>
      <Select
        value={inputValue}
        onChange={e => updateInputValues(valueKey, e.target.value)}
        displayEmpty
        placeholder={componentElement.placeholder}
      >
        {!componentElement.initialValue && componentElement.placeholder && (
          <MenuItem value="" disabled>
            <span className={styles.selectPlaceholder}>{componentElement.placeholder}</span>
          </MenuItem>
        )}
        {selectionItems &&
          selectionItems.map(item => (
            <MenuItem value={item.value} key={`select-${valueKey}-${item.value}`}>
              {item.label}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};

export default React.memo(SelectItemElement);
